import {
  ADDED_BY_YOU_KEY,
  type AgendaConcernGroup,
  type AgendaGroupableItem,
  PREVENTIVE_CARE_KEY,
} from "./agenda-grouping";

export type AgendaHeaderCode = {
  code: string;
  description?: string | null;
};

export type AgendaConcernHeaderModel = {
  key: string;
  title: string;
  shownCodes: AgendaHeaderCode[];
  overflowCount: number;
};

/**
 * Cap concern-header medical codes at three visible codes with +k overflow.
 */
export function formatConcernHeaderCodes(
  codes: readonly AgendaHeaderCode[],
  maxVisible = 3,
): { shownCodes: AgendaHeaderCode[]; overflowCount: number } {
  const shownCodes = codes.slice(0, maxVisible);
  return {
    shownCodes,
    overflowCount: Math.max(0, codes.length - shownCodes.length),
  };
}

/**
 * Build the concern-section header model from a grouped agenda bucket.
 */
export function toAgendaConcernHeader<
  T extends AgendaGroupableItem & {
    healthConcern?: { id: number; name: string } | null;
    medicalCodes?: readonly AgendaHeaderCode[] | null;
  },
>(group: AgendaConcernGroup<T>): AgendaConcernHeaderModel {
  if (group.key === ADDED_BY_YOU_KEY) {
    return {
      key: group.key,
      title: "Added by you",
      shownCodes: [],
      overflowCount: 0,
    };
  }

  if (group.key === PREVENTIVE_CARE_KEY) {
    return {
      key: group.key,
      title: "Preventive Care",
      shownCodes: [],
      overflowCount: 0,
    };
  }

  const first = group.items[0];
  const { shownCodes, overflowCount } = formatConcernHeaderCodes(
    first?.medicalCodes ?? [],
  );

  return {
    key: group.key,
    title: first?.healthConcern?.name ?? "Health concern",
    shownCodes,
    overflowCount,
  };
}
